import { useEffect, useRef } from 'react';

import { addDatadogRUMGlobalContext } from '@opendoor/observability/slim';
import { useObservability } from 'platform/observability';
import type { useSellerTransaction } from 'sellDirect/graphql/useSellerTransaction';

type SellerTransactionResponseForFirstResultTelemetry = Pick<
  ReturnType<typeof useSellerTransaction>,
  'sellerTransaction' | 'loading' | 'error'
>;

/**
 * After the first settled, successful SellerTransaction fetch for a route address, sets RUM context and
 * emits `seller-transaction-first-result` once.
 * Polling updates are ignored per addressUUID until remount or address change.
 */
export const useTrackSellerTransactionFirstResult = (
  addressUUID: string | undefined,
  sellerTransactionResponse: SellerTransactionResponseForFirstResultTelemetry,
): void => {
  const { trackEvent } = useObservability();
  const loggedForAddressRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!addressUUID) {
      return;
    }
    if (sellerTransactionResponse.loading || sellerTransactionResponse.error) {
      return;
    }
    if (loggedForAddressRef.current === addressUUID) {
      return;
    }
    loggedForAddressRef.current = addressUUID;

    const sellerTransactionType =
      sellerTransactionResponse.sellerTransaction?.['__typename'] ?? 'null';

    addDatadogRUMGlobalContext({
      sellerTransactionFirstResultType: sellerTransactionType,
    });
    trackEvent('seller-transaction-first-result', sellerTransactionType, {
      sellerTransactionType,
      addressUUID,
    });
  }, [
    addressUUID,
    sellerTransactionResponse.loading,
    sellerTransactionResponse.error,
    sellerTransactionResponse.sellerTransaction,
    trackEvent,
  ]);
};
